import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { AuthProvider } from './contexts/AuthContext';
import { MessagingProvider } from './contexts/MessagingContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { FeatureProvider, FeatureGate } from './contexts/FeatureContext';
import { useAuth } from './contexts/AuthContext';
import { useFeature } from './middleware/useFeature';
import PrivateRoute from './components/PrivateRoute';
import PostCallForm from './components/PostCallForm';
import AnnouncementBanner from './components/AnnouncementBanner';
import 'react-toastify/dist/ReactToastify.css';
import './styles/App.css';

// ── Lazy-loaded routes ────────────────────────────────────────────────────────
const Login                  = lazy(() => import('./components/Login'));
const Register               = lazy(() => import('./components/Register'));
const Dashboard              = lazy(() => import('./components/Dashboard'));
const TaskDetails            = lazy(() => import('./components/TaskDetails'));
const Messaging              = lazy(() => import('./components/Messaging'));
const Settings               = lazy(() => import('./components/Settings'));
const JoinTeam               = lazy(() => import('./components/JoinTeam'));
const ExpenseReimbursement   = lazy(() => import('./components/ExpenseReimbursement'));
const SupportTicketsDashboard = lazy(() => import('./components/SupportTicketsDashboard'));
const SuperAdminLogin        = lazy(() => import('./components/SuperAdminLogin'));
const SuperAdminDashboard    = lazy(() => import('./components/SuperAdminDashboard'));

const Policies               = lazy(() => import('./pages/Policies'));
const PolicyDetails          = lazy(() => import('./pages/PolicyDetails'));
const CreatePolicy           = lazy(() => import('./pages/CreatePolicy'));
const SalesPage              = lazy(() => import('./pages/SalesPage'));
const CrmPage                = lazy(() => import('./pages/CrmPage'));
const LeadsPage              = lazy(() => import('./pages/LeadsPage'));
const AccountsPage           = lazy(() => import('./pages/AccountsPage'));
const NotificationsHistory   = lazy(() => import('./pages/NotificationsHistory'));
const MarketplacePage        = lazy(() => import('./pages/MarketplacePage'));
const GoalsPage              = lazy(() => import('./pages/GoalsPage'));
const LMSPerformancePage     = lazy(() => import('./pages/LMSPerformancePage'));
const RawMaterialPage        = lazy(() => import('./pages/RawMaterialPage'));
const BugReportsPage         = lazy(() => import('./pages/BugReportsPage'));
const LocationTrackingPage   = lazy(() => import('./pages/LocationTrackingPage'));
const FaceClockInPage        = lazy(() => import('./pages/FaceClockInPage'));
const AINotesPage            = lazy(() => import('./pages/AINotesPage'));

const FeatureRoute = ({ feature, children }) => {
  const enabled = useFeature(feature);
  return enabled ? children : <Navigate to="/dashboard" replace />;
};

const SuperAdminRoute = ({ children }) =>
  localStorage.getItem('super_token') ? children : <Navigate to="/admin-portal-xyz789" replace />;

const PageTitleUpdater = () => {
  const location = useLocation();

  useEffect(() => {
    const getTitle = (pathname) => {
      if (pathname === '/' || pathname === '/dashboard') return 'Dashboard';
      if (pathname === '/messages') return 'Chats';
      if (pathname.startsWith('/messages/')) return 'Chat';
      if (pathname === '/settings') return 'Settings';
      if (pathname === '/crm') return 'CRM';
      if (pathname === '/accounts') return 'Accounts';
      if (pathname === '/reimbursements') return 'Reimbursements';
      if (pathname === '/sales') return 'Sales';
      if (pathname === '/policies') return 'Policies';
      if (pathname.startsWith('/policies/')) return 'Policy Details';
      if (pathname === '/create-policy') return 'Create Policy';
      if (pathname.startsWith('/task/')) return 'Task Details';
      if (pathname === '/login') return 'Login';
      if (pathname === '/register') return 'Register';
      if (pathname === '/join') return 'Join Team';
      if (pathname === '/notifications-history') return 'Notification History';
      if (pathname === '/support-tickets') return 'Support Tickets';
      if (pathname === '/marketplace') return 'Marketplace';
      if (pathname === '/raw-materials') return 'Raw Materials';
      if (pathname === '/bug-reports') return 'Bug Tracker';
      if (pathname === '/location-tracking') return 'GPS Tracking';
      if (pathname === '/face-clockin') return 'Face Clock-In';
      if (pathname === '/ai-notes') return 'AI Notes';
      if (pathname === '/goals') return 'Goals & Targets';
      if (pathname === '/lms-performance') return 'Performance Analytics';
      return 'DeskPanda';
    };

    document.title = `${getTitle(location.pathname)} - DeskPanda`;
  }, [location.pathname]);

  return null;
};

const EntitlementAwareFeatureProvider = ({ children }) => {
  const { entitlements } = useAuth();
  return (
    <FeatureProvider overrides={entitlements?.features || {}}>
      {children}
    </FeatureProvider>
  );
};

function App() {
  return (
    <AuthProvider>
    <EntitlementAwareFeatureProvider>
      <Router>
      <MessagingProvider>
        <PageTitleUpdater />
      <NotificationProvider>
        <div className="app">
          <Suspense fallback={null}>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/task/:id" element={<TaskDetails />} />
              <Route path="/register" element={<Register />} />
              <Route path="/join" element={<JoinTeam />} />
              <Route path="/policies" element={<PrivateRoute><Policies /></PrivateRoute>} />
              <Route path="/policies/:id" element={<PrivateRoute><PolicyDetails /></PrivateRoute>} />
              <Route path="/create-policy" element={<PrivateRoute><CreatePolicy /></PrivateRoute>} />
              <Route path="/sales" element={<PrivateRoute><SalesPage /></PrivateRoute>} />
              <Route path="/messages" element={<PrivateRoute><Messaging /></PrivateRoute>} />
              <Route path="/settings" element={<PrivateRoute><Settings /></PrivateRoute>} />
              <Route path="/crm" element={<PrivateRoute><CrmPage /></PrivateRoute>} />
              <Route path="/leads" element={<PrivateRoute><FeatureRoute feature="lms"><LeadsPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/goals" element={<PrivateRoute><FeatureRoute feature="lms"><GoalsPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/lms-performance" element={<PrivateRoute><FeatureRoute feature="lms"><LMSPerformancePage /></FeatureRoute></PrivateRoute>} />
              <Route path="/accounts" element={<PrivateRoute><AccountsPage /></PrivateRoute>} />
              <Route path="/reimbursements" element={<PrivateRoute><ExpenseReimbursement /></PrivateRoute>} />
              <Route path="/support-tickets" element={<PrivateRoute><SupportTicketsDashboard /></PrivateRoute>} />
              <Route path="/marketplace" element={<PrivateRoute><MarketplacePage /></PrivateRoute>} />
              <Route path="/raw-materials" element={<PrivateRoute><FeatureRoute feature="raw_materials"><RawMaterialPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/bug-reports" element={<PrivateRoute><FeatureRoute feature="bug_reports"><BugReportsPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/location-tracking" element={<PrivateRoute><FeatureRoute feature="gps_tracking"><LocationTrackingPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/face-clockin" element={<PrivateRoute><FeatureRoute feature="face_clockin"><FaceClockInPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/ai-notes" element={<PrivateRoute><FeatureRoute feature="ai_notes"><AINotesPage /></FeatureRoute></PrivateRoute>} />
              <Route path="/admin-portal-xyz789" element={<SuperAdminLogin />} />
              <Route path="/super-dashboard" element={<SuperAdminRoute><SuperAdminDashboard /></SuperAdminRoute>} />
              <Route
                path="/dashboard"
                element={
                  <PrivateRoute>
                    <Dashboard />
                  </PrivateRoute>
                }
              />
              <Route
                path="/notifications-history"
                element={
                  <PrivateRoute>
                    <NotificationsHistory />
                  </PrivateRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Suspense>
          <AnnouncementBanner />
          <FeatureGate feature="phone_dialer"><PostCallForm /></FeatureGate>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop={true}
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </div>
      </NotificationProvider>
      </MessagingProvider>
      </Router>
    </EntitlementAwareFeatureProvider>
    </AuthProvider>
  );
}

export default App;
